import { useWarningStore } from "~/store/globalMessage";
import { useMasters } from "~/store/masters";
import { useUserStore } from "~/store/auth";
import { useGlobalScheduling } from "~/store/globalScheduling";
import WWSS from "~/utils/wwss";
import { BACKEND_LOCALES, FRONTEND_LOCALES } from "~/utils/constants";
import { watch, nextTick } from "vue";

const MAXIMO_WRITE_OPERATIONS = new Set([
  "create",
  "modify",
  "modify_extra",
  "delete",
  "set",
  "deactivate",
  "activate",
]);

export const SATURATION_RANGES = [
  { min: 0, max: 50, color: "red-lighten-3" },
  { min: 50, max: 90, color: "amber-lighten-2" },
  { min: 90, max: 130, color: "green-lighten-3" },
  { min: 130, max: Infinity, color: "red-lighten-3" },
];

// Función genérica para realizar las solicitudes
const fetchData = async ({ url, method, bodyOrParams, headers = {} }) => {
  const config = useRuntimeConfig();
  const user = useUserStore();
  const startTime = performance.now();
  const userId =
    user?.login?.CodOperarioMaximo ||
    JSON.parse(localStorage.getItem("profile"))?.PERSONID ||
    "unknown";
  let success = true;
  let responseCode = 200;
  let responseData = "";
  const forwardedHeaders = Object.fromEntries(
    Object.entries(headers || {}).filter(
      ([name]) => !["api-token", "api-product"].includes(name.toLowerCase())
    )
  );

  try {
    let options = {
      method,
      headers: {
        "API-VERSION": config.public.headers.apiVersion,
        "Content-Type": "application/json",
        ...forwardedHeaders,
      },
    };

    // Si el método es POST, añadimos el cuerpo
    if (method === "POST") {
      options.body = bodyOrParams;

      const operation = url.split("/").pop();
      if (
        url.startsWith("/maximo/") &&
        MAXIMO_WRITE_OPERATIONS.has(operation)
      ) {
        const locale = useNuxtApp().$i18n.locale.value;
        const lang =
          locale === FRONTEND_LOCALES.EN
            ? BACKEND_LOCALES.EN
            : BACKEND_LOCALES.ES;
        options.body = Array.isArray(bodyOrParams)
          ? bodyOrParams.map((item) => ({ ...item, lang }))
          : { ...bodyOrParams, lang };
      }
    }

    // Si el método es GET, añadimos los parámetros
    if (method === "GET") {
      options.params = bodyOrParams;
    }

    // Excepción para rutas que comienzan con "/suite", ya que estas deben ir a un endpoint diferente
    let targetUrl = `api${url}`;
    if (url.startsWith("/suite")) {
      
      // Establecemos URL destino, que ya incluye el prefijo "/suite" y no necesita "api"
      targetUrl = `${url}`;

      // Modificación de headers para rutas de suite
      options.headers = {
        "Content-Type": "application/json",
        "APP-BUNDLE": config.public.headers.suiteAppBundle,
        "APP-LANGUAGE": "es_ES",
        ...forwardedHeaders,
      };

    }

    const data = await $fetch(targetUrl, options);

    // Si hay un error en la respuesta, tratamos la respuesta como un fallo
    if (data.error) {
      responseCode = data.error.code || 500;
      responseData = data;
      success = false;
    } else {
      responseData = data; // Guardamos la respuesta en caso de éxito
    }

    return data;
  } catch (error) {
    // Manejamos los errores de la solicitud
    success = false;
    responseCode = error?.response?.status || 500;
    responseData = error.message || "unknown error";

    console.error("Error en la autenticación del backend", error);
    return {
      error: {
        code: error?.response?.data?.code || "backend.default",
        message:
          error?.response?.data?.message || error?.data?.message || error?.message,
      },
    };
  } finally {
    // Calculamos la duración de la solicitud
    const duration = performance.now() - startTime;

    // Registramos los datos en Application Insights
    const logDataFinal = {
      method,
      url,
      duration,
      body: bodyOrParams, // Pasamos el cuerpo o los parámetros, según corresponda
      success,
      responseCode,
      status: `${responseCode} (${success ? "Success" : "Failed"})`,
      userId,
    };

    setApplicationInsightLog(logDataFinal);
  }
};

// Función específica para POST
export const fetchPostData = async (url, body, options = {}) => {
  return fetchData({
    url,
    method: "POST",
    bodyOrParams: body,
    headers: options.headers,
  });
};

// Función específica para GET
export const fetchGetData = async (url, params) => {
  return fetchData({
    url,
    method: "GET",
    bodyOrParams: params,
  });
};

function setApplicationInsightLog(logData) {
  const { $appInsights } = useNuxtApp();

  const { url, method, success, responseCode, duration, userId, body } =
    logData;

  if ($appInsights) {
    $appInsights.trackDependencyData({
      target: `api${url}`,
      name: method + " " + url,
      type: "Fetch",
      duration: duration,
      success: success, // Se asigna el valor de success calculado previamente
      responseCode: responseCode, // Código de respuesta (puede ser 200 o un código de error)
      properties: {
        user: userId, // El ID del usuario, si es necesario
        HttpMethod: method,
        status: responseCode, // Código de respuesta
        // responseData: responseData, // Datos de la respuesta
        body: body, // El cuerpo enviado en la solicitud
      },
    });
  }
}

export async function startStatement(endpoint, params) {
  return await fetchPostData(endpoint, params);
}

// Función para obtener los datos una vez el trabajo ha finalizado
export async function getStatementPending(statement_id) {
  const response = await fetchPostData(WWSS.databricks.get_statement_pending, {
    statement_id,
  });
  return response;
}

export const fetchDatabricksWithRetry = async (
  endpoint,
  params,
  retries = 5,
  delay = 2000
) => {
  const checkJobStatus = async (job_result, retriesLeft) => {
    for (let i = 0; i < retriesLeft; i++) {
      if (
        job_result.status.state === "PENDING" ||
        job_result.status.state === "RUNNING"
      ) {
        job_result = await getStatementPending(job_result.statement_id);
      } else {
        return job_result;
      }

      // Espera antes de volver a intentar
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    // Si se agotan los reintentos, retornar el último resultado obtenido
    return job_result;
  };

  try {
    // Inicia el trabajo y obtiene el jobId
    let job_result = await startStatement(endpoint, params);

    // Verificar el estado del trabajo hasta que tenga éxito o se agoten los reintentos
    job_result = await checkJobStatus(job_result, retries);

    // Devolver el resultado final obtenido
    return job_result;
  } catch (error) {
    console.error("Error in fetchDataWithRetry:", error);
    throw error;
  }
};

/**
 * Función gestión de databricks
 * @returns
 */

export const useDatabricksService = () => {
  const requestState = ref({
    status: "idle",
    data: null,
    error: null,
  });

  const makeRequest = async (url, params) => {
    requestState.status = "pending";

    try {
      const response = await $fetch(`api${url}`, {
        method: "GET",
        params,
        headers: {
          "API-VERSION": config.public.headers.apiVersion,
          "Content-Type": "application/json",
        },
        timeout: 10000,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      requestState.status = "success";
      requestState.data = data;
      requestState.error = null;
    } catch (error) {
      requestState.status = "error";
      requestState.error = error.message;
      console.error("Error al realizar la solicitud:", error);
    }
  };

  return {
    requestState,
    makeRequest,
  };
};

/**
 * Devuelve un array ordenado de manera ascendente por una se las propiedades de los objetos
 * @param {Array} array - Array de objetos a ordenar
 * @param {String} property - Nombre de la propiedad por la cual ordenar
 * @param {String} order - Orden de clasificación: 'asc' para ascendente o 'desc' para descendente
 * @returns {Array} - Array ordenado
 */
export const sortElements = (array, property, order = "asc") => {
  return array.sort((a, b) => {
    const propertyA = (a[property] || "").toUpperCase();
    const propertyB = (b[property] || "").toUpperCase();

    let comparison = 0;
    if (propertyA > propertyB) {
      comparison = 1;
    } else if (propertyA < propertyB) {
      comparison = -1;
    }
    return order === "desc" ? comparison * -1 : comparison;
  });
};

/**
 * Ordena un array de objetos por una propiedad de fecha
 * @param {Array} data
 * @param {String} property
 * @param {String} order
 * @returns
 */
export const sortByDate = (data = [], property, order = "asc") => {
  return [...data].sort((a, b) => {
    const dateA = parseDateAsUTC(a[property]);
    const dateB = parseDateAsUTC(b[property]);

    return order === "asc"
      ? dateA - dateB // más antiguo → más reciente
      : dateB - dateA; // más reciente → más antiguo
  });
};

export const generateMessageType = (respuestas, successfulMsg) => {
  const msgErrores = [];
  const msgCorrectas = [];
  const correctas = [];

  const globalMessage = useWarningStore();

  // generar respuesta con errores y con correctas
  for (const res in respuestas) {
    if (respuestas[res].error) {
      msgErrores.push(`${res} :  ${respuestas[res].error.message}`);
    } else {
      correctas.push(respuestas[res]);
      successfulMsg != ""
        ? msgCorrectas.push(`${res} : ${successfulMsg} `)
        : msgCorrectas.push(`${res}`);
    }
  }

  if (msgErrores.length > 0 && msgCorrectas.length == 0) {
    globalMessage.showGenericErrorMessage(msgErrores);
  } else if (msgCorrectas.length > 0 && msgErrores.length == 0) {
    globalMessage.showGenericSuccessMessage(msgCorrectas);
  } else if (msgCorrectas.length > 0 && msgErrores.length > 0) {
    globalMessage.showGenericWarningMessage([...msgErrores, ...msgCorrectas]);
  }
  return correctas;
};

export const beforeUnloadHandler = (event) => {
  event.preventDefault();
  event.returnValue = "";
};

// Función para eliminar duplicados de un array de objetos basado en una propiedad clave
export const removeDuplicates = (array, key) => {
  const seen = new Set();
  return array.filter((item) => {
    const value = item[key];
    if (seen.has(value)) {
      return false;
    } else {
      seen.add(value);
      return true;
    }
  });
};

export const searchInString = (stringToCheck, arrayToCheck) => {
  if (typeof stringToCheck !== "string") {
    return false;
  }
  return arrayToCheck.some((item) =>
    stringToCheck.toLowerCase().includes(item.toLowerCase())
  );
};

export const countAppliedFilters = (filters) => {
  const globalScheduling = useGlobalScheduling();
  let count = 0;

  for (const value of Object.values(filters)) {
    if (
      (Array.isArray(value) && value.length > 0) ||
      (typeof value === "boolean" && value) ||
      (typeof value === "string" && value !== "" && value !== null)
    ) {
      count++;
    }
  }

  globalScheduling.isFiltered = count > 0;

  return count;
};

export const getMateCompleteName = (
  mate_id,
  showLaborCode = true,
  showPersonGroup = false
) => {
  const masters = useMasters();

  // Encuentra el objeto correspondiente al mate_id
  const mate_data = masters.mates.list.find((m) => m.LABORID === mate_id);

  if (mate_data) {
    // Construye el nombre completo
    const first = (mate_data.FIRSTNAME || "").trim();
    const last = (mate_data.LASTNAME || "").trim();

    // Solo arma el nombre si alguno tiene contenido
    const name = [first, last].filter(Boolean).join(" ").trim();
    const nameParts = [
      showLaborCode ? mate_id : null,
      showPersonGroup ? mate_data.DEFAULTPERSONGROUP : null,
      name,
    ].filter(Boolean); // Filtra valores nulos, undefined o vacíos

    return nameParts.length > 0 ? nameParts.join(" · ") : mate_id;
  }

  // Si no se encuentra el mate_data, retorna el mate_id como está
  return mate_id;
};

/**
 * Elimina las tildes de vocales para búsquedas y filtrados
 * @param {String} text
 * @returns
 */
export const removeDiacritics = (text) => {
  return text.normalize("NFD").replace(/([aeiouAEIOU])[\u0300-\u0301]/g, "$1"); // Eliminar solo las tildes
};

/**
 * Copia profunda de objeto
 * @param {Object} object
 * @returns objeto
 */
export const cloneDeepObject = (object) => {
  return JSON.parse(JSON.stringify(object));
};

/**
 *
 * @param {*} val
 * @returns
 */
export const toBool = (val) => val === true || val === 1 || val === "1";

/**
 * Composable general con utilidades de sanitización
 *
 * @returns {{ sanitizePlain: Function, sanitizeRich: Function }}
 */
export const useSanitize = () => {
  const { $sanitize } = useNuxtApp(); // viene del plugin dompurify.js

  /**
   * Decodifica secuencias tipo \x3C o \u003C para convertirlas en < >
   */
  const decodeJsEscapes = (str) => {
    if (typeof str !== "string") return "";
    // \uHHHH
    str = str.replace(/\\u([0-9a-fA-F]{4})/g, (_, h) =>
      String.fromCharCode(parseInt(h, 16))
    );
    // \xHH
    str = str.replace(/\\x([0-9a-fA-F]{2})/g, (_, h) =>
      String.fromCharCode(parseInt(h, 16))
    );
    return str;
  };

  /**
   * Sanitiza a texto plano (elimina TODAS las etiquetas,
   * conserva el contenido interno gracias a KEEP_CONTENT)
   */
  const sanitizePlain = (val) => {
    const out = $sanitize(decodeJsEscapes(String(val ?? "")), {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
      KEEP_CONTENT: true,
    });
    // normaliza espacios por si había etiquetas pegadas
    return out.replace(/\s+/g, " ").trim();
  };

  /**
   * Sanitiza permitiendo un subconjunto de formato básico
   * (negritas, cursivas, enlaces, saltos de línea...)
   * Conserva contenido de etiquetas prohibidas
   */
  const sanitizeRich = (val) =>
    $sanitize(decodeJsEscapes(String(val ?? "")), {
      USE_PROFILES: { html: true },
      ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "a", "br", "p"],
      ALLOWED_ATTR: ["href", "title", "target", "rel"],
      FORBID_TAGS: ["script", "style"],
      KEEP_CONTENT: true,
      ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:)/i,
    });

  return { sanitizePlain, sanitizeRich };
};

export const delay = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export function useCalendarObserver(viewRef, navRef) {
  watch(viewRef, async (newView, oldView) => {
    if (newView !== oldView) {
      // 1. Mandamos limpiar el componente antes de que cambie el DOM
      if (navRef.value?.cleanup) {
        navRef.value.cleanup();
      }

      // 2. Esperamos que Vue actualice el DOM (v-show / v-if)
      await nextTick();

      // 3. Re-arrancamos la búsqueda
      if (navRef.value?.setupObservers) {
        navRef.value.setupObservers();
      } else {
        console.warn("[Observer] NavRef no disponible.");
      }
    }
  });

  // Limpieza extra si el composable se destruye
  onUnmounted(() => {
    if (navRef.value?.cleanup) {
      navRef.value.cleanup();
    }
  });
}
